import { createFileRoute, notFound } from '@tanstack/react-router'
import BlogPost from '@/components/blog/blog-post'
import { getArticle, getCategories, getRecentPostsByAuthor, getAllTags, getRelatedPosts } from '@/lib/sanity.server'
import { createArticleSchema, createBreadcrumbSchema, createFAQSchema, createLLMOMeta } from '@/lib/seo'
import CTA from '@/components/LandingPage/CTA'

export const Route = createFileRoute('/article/$slug')({
  loader: async ({ params }) => {
    const article = await getArticle({ data: params.slug })
    if (!article) {
      throw notFound()
    }

    const [categories, recentPosts, popularTags, relatedPosts] = await Promise.all([
      getCategories(),
      article.author?.slug
        ? getRecentPostsByAuthor({ data: { authorSlug: article.author.slug, currentId: article.id } })
        : Promise.resolve([]),
      getAllTags(),
      article.category?.slug
        ? getRelatedPosts({ data: { currentId: article.id, categoryId: article.category.slug } })
        : Promise.resolve([]),
    ])

    return { article, categories, recentPosts, popularTags, relatedPosts }
  },
  head: ({ loaderData }) => {
    const article = loaderData?.article
    if (!article) return {}

    const title = article.seo?.metaTitle || `${article.title} | Zapify Blog`
    const description = article.seo?.metaDescription || article.description || "Read the latest insights from Zapify"
    const pageUrl = `https://zapify.pro/article/${article.slug}`
    const canonicalUrl = article.seo?.canonicalUrl || pageUrl
    const image = article.seo?.ogImage?.url || article.cover?.url || "https://zapify.pro/zapify-ogp.png"
    const imageWidth = article.seo?.ogImage?.width ?? article.cover?.width
    const imageHeight = article.seo?.ogImage?.height ?? article.cover?.height
    const imageAlt = article.seo?.ogImage?.alternativeText || article.cover?.alternativeText || article.title
    const authorName = article.author?.name || "Zapify Team"
    const faqSchema = article.faqs?.length ? [createFAQSchema(article.faqs)] : []
    const focusKeywordMeta = article.seo?.focusKeyword
      ? [{ name: "keywords", content: article.seo.focusKeyword }]
      : []

    return {
      title,
      meta: [
        ...createLLMOMeta({
          title,
          description,
          url: canonicalUrl,
          image,
          author: authorName,
          publishedTime: article.createdAt,
          modifiedTime: article.updatedAt,
          noIndex: article.seo?.noIndex,
        }),
        ...focusKeywordMeta,
        { property: "og:image", content: image },
        { property: "og:image:width", content: imageWidth?.toString() || "1200" },
        { property: "og:image:height", content: imageHeight?.toString() || "630" },
        { property: "og:image:alt", content: imageAlt },
        { property: "og:locale", content: "en_US" },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl }
      ],
      scripts: [
        createArticleSchema({
          headline: article.title,
          description,
          image,
          author: {
            name: authorName,
            url: `https://zapify.pro`,
          },
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          publisher: {
            name: 'Zapify',
            url: 'https://zapify.pro',
            logo: 'https://zapify.pro/logo.png',
            description: 'AI-Powered Instagram Comment Management & Automation',
          },
        }),
        ...faqSchema,
        createBreadcrumbSchema({
          itemListElement: [
            {
              position: 1,
              name: 'Home',
              item: 'https://zapify.pro',
            },
            {
              position: 2,
              name: 'Blog',
              item: 'https://zapify.pro/blog',
            },
            {
              position: 3,
              name: article.title,
              item: pageUrl,
            },
          ],
        }),
      ],
    }
  },
  component: ArticlePage,
  notFoundComponent: () => {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-black text-gray-900 dark:text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400">The article you are looking for does not exist.</p>
        </div>
      </div>
    )
  }
})

function ArticlePage() {
  const { article, categories, recentPosts, popularTags, relatedPosts } = Route.useLoaderData()
  return (
    <>
      <BlogPost article={article} categories={categories} recentPosts={recentPosts} popularTags={popularTags} relatedPosts={relatedPosts} />
      <CTA />
    </>
  )
}
